<template lang='pug'>
  //- fixed-top allows for the topbar to be correctly padded when the scollbar is hidden as it is a selector used by the modal helpers
  .topbar.fixed-top.js-topbar
    .container
      ul.nav-container
        template(v-if='!!currentUser')
          li.nav-item.account
            MyAccountDropdown(
              :signedIn='true'
              :userEmail='currentUser.attributes.email'
              :userFirstName='currentUser.attributes["first-name"]'
            )
          li.nav-item.points
            span.nav-link.divider |
            a.nav-link(:href='"/account/point_logs"' v-text='`${currentUser.attributes["points-formatted"]} ` + $t("account.pointLogs.points")')

          li.nav-item.onboarding(v-if='displayGetStartedCTA')
            a.nav-link.get-started(href='/account/user-onboarding' ref='getStarted' @click='trackGetStartedClick')
              span.heart-icon
              span.text(v-t='"account.onboarding.getStarted"')

        template(v-else)
          li.nav-item.account
            MyAccountDropdown(:signedIn='false')
          li.nav-item.sign-in-register-links
            AuthPopupLink(:classes='["navlink", "register-link"]' type='register')
            span.nav-link.divider |
            AuthPopupLink(:classes='["navlink", "sign-in-link"]' type='signIn')

        li.nav-space
        template(v-if='showGetAppSection')
          li.nav-item.get-app.app-item
            a.nav-link.js-get-app-header-link(href='#' @click.prevent.stop='toggleGetAppPanel')
              span.get-app-icon
              span.icon-label(v-text='$t("navigation.links.getApp")')
            GetAppDropdown(v-if='showGetAppPanel')

        template(v-if='showStoreLocations')
          template(v-if='showOnlineReservationSystem')
            BookingServiceDropdown(ref='bookingServiceDropdown' @closeDropdowns='closeDropdowns')

          template(v-else)
            li.nav-item.stores
              a.nav-link(:href='"/store_locations"')
                span.store.svg-icon.store-white
                span.icon-label(v-text='$t("retailStore.findStore")')

        li.nav-item.current-country-details(v-if='crossBorderCountry')
          CountryDetails
        li.nav-item.language-preference(v-if='Object.keys(languages).length > 1')
          LanguageDropdown
          UserPreference(:full='true')

        li.nav-item.wish-list-heart
          router-link.nav-link(:to='{ name: "wishlist" }')
            span.wish-list-heart-filled.svg-icon.heart-fill

        li.nav-item.top-cart(ref='cartIcon' @click.prevent.stop='viewCart')
          a.cart-link.nav-link(:href='"/cart"' rel='nofollow')
            span.cart-bag.svg-icon.bag-white
            span.icon-label(v-text='$t("navigation.links.bag")')
            span.notranslate.count(v-show='lineItemsCount > 0' v-text='lineItemsCount')

        CartDropdown(
          v-show='showCartDropdown'
          @closeDropdowns='closeDropdowns'
        )
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { searchUtil } from '@/mixins/layoutUtil'
import LanguageDropdown from '@/components/layout/header/language-dropdown.vue'
import CountryDetails from '@/components/layout/header/country-details.vue'
import UserPreference from '../user-preference.vue'
import MyAccountDropdown from './my-account-dropdown.vue'
import CartDropdown from './cart-dropdown.vue'
import AuthPopupLink from '../auth-popup-link.vue'
import GetAppDropdown from './get-app-dropdown.vue'
import BookingServiceDropdown from './booking-service-dropdown.vue'
import VisibilityObserver from '@/modules/services/visibility-observer'
import { pushDataLayerEvent } from '@/modules/services/gtm'

export default {
  components: {
    LanguageDropdown,
    UserPreference,
    MyAccountDropdown,
    CartDropdown,
    AuthPopupLink,
    GetAppDropdown,
    BookingServiceDropdown,
    CountryDetails
  },

  mixins: [
    searchUtil
  ],

  data () {
    return {
      showGetAppPanel: false,
      observer: this.visibilityObserver(),
      element: null
    }
  },

  computed: {
    ...mapState({
      currentUser: state => state.user.currentUser,
      showCartDropdown: state => state.cart.showCartDropdown,
      cart: state => state.cart.cart
    }),
    ...mapGetters(['lineItemsCount', 'displayGetStartedCTA']),
    showStoreLocations: () => vueSupportBookingServices.storeLocations,
    showOnlineReservationSystem: () => vueSupportBookingServices.supportOnlineReservationSystem,
    languages: () => vueSupport.header.languages,
    userReturnTo () {
      if (this.$route.meta.returnTo) {
        return `?return_to=${this.$route.path}`
      } else {
        return ''
      }
    },
    showGetAppSection () {
      return this.showGetAppIcon && vueSupport.header.showGetAppSection
    },
    showGetAppIcon () {
      return (
        this.$t('app.android.header', I18n.siteCountryLs) !== '' ||
        this.$t('app.ios.header', I18n.siteCountryLs) !== ''
      )
    },
    crossBorderCountry: () => vueSupport.crossBorderCountry
  },

  created () {
    window.addEventListener('click', this.closeDropdowns)
    if (!this.cart && !this.ignoreCartItems()) {
      this.$store.dispatch('getLineItemsCount')
    }
  },

  mounted () {
    this.element = this.$refs['getStarted']
    if (this.element && this.observer) {
      this.observer.observe(this.element)
    }
  },

  destroyed () {
    window.removeEventListener('click', this.closeDropdowns)
    if (this.observer) {
      this.observer.disconnect()
    }
  },

  methods: {
    viewCart () {
      this.showGetAppPanel = false
      this.$store.commit('setShowCartDropdown', !this.showCartDropdown)

      this.closeBookingServiceDropdown()
      this.closeSearch()
    },

    toggleGetAppPanel () {
      this.showGetAppPanel = !this.showGetAppPanel
      this.$store.commit('setShowCartDropdown', false)

      this.closeBookingServiceDropdown()
      this.closeSearch()
    },

    closeDropdowns () {
      this.showGetAppPanel = false
      this.$store.commit('setShowCartDropdown', false)
    },

    closeBookingServiceDropdown () {
      if (Object.prototype.hasOwnProperty.call(this.$refs, 'bookingServiceDropdown')) {
        this.$refs.bookingServiceDropdown.close()
      }
    },

    ignoreCartItems () {
      return this.$route.path.includes('/confirmations/new_email_verification')
    },

    visibilityObserver () {
      const opts = {
        intersectionRatio: 1.0,
        rootMargin: '0px 0px 0px 0px'
      }
      return VisibilityObserver(this.recordImpressionEvent, opts)
    },

    recordImpressionEvent () {
      pushDataLayerEvent('sign_up_get_started_impression')
      if (this.observer) {
        this.observer.disconnect()
      }
    },

    trackGetStartedClick () {
      pushDataLayerEvent('sign_up_click_get_started')
    }
  }
}
</script>

<style lang='scss' scoped>
  .topbar {
    background-color: $black;
    position: fixed;
    top: 0;
    box-sizing: border-box;
    height: 35px;
    width: 100%;
    z-index: $zindex-fixed;
    font-size: 12px;
    color: $white;

    .container {
      height: 100%;
    }

    // This positioning affects the count icon for the bag
    .cart-link {
      position: relative;

      // For label alignment due to different icon size
      .icon-label {
        position: relative;
      }
    }

    .divider {
      margin-left: 3px;
      margin-right: 3px;
    }

    .nav-container {
      height: 100%;
      display: flex;
      align-items: center;

      .get-app, .stores {
        margin-right: 15px;
      }
    }

    .nav-space {
      flex-grow: 1;
    }

    .nav-item {
        @include font-weight-medium;
        text-transform: uppercase;
        font-size: 12px;
        white-space: nowrap;
    }

    .get-app {
      position: relative;
    }

    .sign-in-register-links {
      margin-left: 20px;
      margin-top: 4px;

      @include media-breakpoint-only(md) {
        margin-right: -5px;
      }
    }

    .language-preference {
      text-align: center;
      margin-right: 15px;
      margin-top: 4px;
    }
  }

  .top-cart {
    padding: 0;

    .count {
      width: 12px;
      height: 8px;
      padding: 4px 2px 4px;
      border-radius: 12px;
      position: absolute;
      z-index: 10;
      font-size: 11px;
      text-align: center;
      line-height: 12px;
      color: #fff;
      display: block;
      top: -3px;
      left: -8px;
      background: $brand-primary;
      border: 2px solid black;
      box-sizing: content-box;

      @include media-breakpoint-up(lg) {
        font-size: 9px;
        line-height: 9px;
      }
    }
  }

  .wish-list-heart-filled {
    height: 20px;
    width: 20px;
    background-size: 20px 20px;
    vertical-align: bottom;
    margin-right: 15px;
  }

  .store {
    height: 20px;
    width: 20px;
    background-size: 20px 20px;
    vertical-align: bottom;
    margin-right: 8px;
  }

  .cart-bag {
    width: 25px;
    height: 25px;
    background-size: 25px 25px;
    vertical-align: bottom;
    margin-right: 3px
  }

  .get-app-icon {
    height: 20px;
    width: 20px;
    background-size: 20px 20px;
    vertical-align: bottom;
    display: inline-block;
    background-image: url('../../../assets/images/icons/get-app.svg');
    margin-right: 8px;
  }

  .onboarding {
    margin-left: 24px;
  }

  .get-started {
    display: flex;
    align-items: center;
    gap: 8px;
    padding-right: 12px;
    padding-left: 12px;
    background-color: $white;
    border-radius: 24px;

    .heart-icon {
      width: 15px;
      height: 16px;
      background-image: url('../../../assets/images/icons/heart.svg');
      background-repeat: no-repeat;
    }

    > .text {
      font-size: 14px;
      color: $black;
      font-weight: 700;
      text-transform: none;
    }
  }
</style>
